import React, { useCallback, useState } from 'react';
import { useQuery } from '@apollo/client';
import { memoize, isEqual } from 'lodash';
import { EmailAttachment } from './email-attachment';
import { EmailSubject } from './email-subject';
import { NEW_DATE_TIME_FORMAT } from '@atlassian/cssxp-typescommon';
import Button from '@atlaskit/button';
import {
  Email,
  EmailEdge,
  FetchEmailsForTransactionAccountInPpcQuery,
  FetchEmailsForTransactionAccountInPpcQueryVariables,
} from '@css-xp/graphtypes/src/generated-types-new';
import { emptyStateProp, errorStateProp, NoData, TableHeading } from '@css-xp/ppc-common';
import {
  ColGroup,
  CustomDynamicResultTable,
  CustomSearch,
  DateFilterWithBadge,
  DateRangeType,
  FormattedDate,
  TableBody,
  TableHead,
} from '@css-xp/base';
import {
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
  MAX_EMAIL_COUNT,
  routePaths,
  SEARCH_PAGE_LOADER_OPTIONS,
} from '@css-xp/ppc-constants';
import { getFormattedDateFromTimeStamp, getRouteUrl } from '@css-xp/ppc-utils';
import InfoPanel from '@css-xp/base/info-panel/src/info-panel';
import { fetchEmailsForTransactionAccountInPPC } from '../queries/fetch-emails-for-transaction-account';
import { EmailTableColumns, EmailTableProps, FormData, ColumnList, DateRangeState } from './types';
import { emptyStatePropForEmailTable, emptyStatePropForWrongSearch } from '@css-xp/ppc-common/src/state-props';
import './emails-table.scss';

export const EmailTable = ({
  transactionAccountId,
  partnerAccountId,
  isPaginationEnabled,
  label = 'Emails',
  testId,
  attachmentRoute,
  emailDetailsRoute,
  hideFilters = false,
  searchQuery = undefined,
  isDesignIteration1Enabled = false,
  isEmailsImprovementEnabled = false,
}: EmailTableProps) => {
  const [rowsPerPage, setRowsPerPage] = useState<number>(DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState<number>(DEFAULT_PAGE);
  const [selectedDateRangeValue, setSelectedDateRangeValue] = useState<string>('');
  const [dateRange, setDateRange] = useState<DateRangeState>({});
  const [customSearchQuery, setCustomSearchQuery] = useState<string | undefined>(searchQuery);
  const [isSearched, setIsSearched] = useState(!!searchQuery);

  const { data, error, loading, refetch } = useQuery<
    FetchEmailsForTransactionAccountInPpcQuery,
    FetchEmailsForTransactionAccountInPpcQueryVariables
  >(fetchEmailsForTransactionAccountInPPC, {
    variables: {
      id: transactionAccountId,
      partnerAccountId: partnerAccountId,
      searchParams: {
        query: searchQuery,
        searchOptions: { first: DEFAULT_PAGE_SIZE, offset: 0 },
      },
    },
    displayName: 'fetchEmailsForTransactionAccount',
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: !transactionAccountId || !partnerAccountId,
  });

  const hasAttachments = (email: Email) => {
    return email.attachments && email.attachments.length > 0;
  };

  const getEmailDetailsPageLink = (id: string) => {
    if (emailDetailsRoute) return `${emailDetailsRoute}/${id}`;
    return getRouteUrl(routePaths.emailDetails, {
      emailId: id,
      txaId: transactionAccountId,
    });
  };

  const results = data?.transactionAccount;
  const emailsConnection = results?.emails;
  const emailEdges = emailsConnection?.edges || [];
  const totalCount = emailsConnection?.totalCount ?? 0;
  const hasNoResults = totalCount === 0;
  const isDesignIteration1EnabledAndNoResults = isDesignIteration1Enabled && hasNoResults;

  const getHeaders = memoize(() => {
    return [
      {
        name: EmailTableColumns.SUBJECT,
        content: EmailTableColumns.SUBJECT,
        customClassNames: isDesignIteration1Enabled ? 'col-50' : 'col-60',
      },
      {
        name: '',
        content: EmailTableColumns.ATTACHMENT,
        customClassNames: isDesignIteration1Enabled ? 'col-10' : 'col-5',
      },
      {
        name: EmailTableColumns.DATE,
        content: EmailTableColumns.DATE,
        customClassNames: isDesignIteration1Enabled ? '' : 'col-20',
      },
    ];
  });

  /* Events onSearch, onPageChange, onRowCountChange */
  const SEARCH_BOX_HELPER_MESSAGE = isEmailsImprovementEnabled
    ? 'Search by email ID, subject line, sender or receiver name'
    : 'Enter keywords to search emails';
  const SEARCH_PLACEHOLDER_TEXT = 'Search';

  const onSearch = useCallback(
    async (formData: FormData) => {
      // Remove hyphen with whitespace from search input
      // example: "abc - def" will be converted to "abc def" before searching, otherwise Backend will not return any result.
      const searchInput = formData.searchTerm?.replace(/\s-/g, '') || undefined;
      setIsSearched(!!searchInput);
      setCustomSearchQuery(searchInput);
      setCurrentPage(DEFAULT_PAGE);
      refetch({
        searchParams: {
          query: searchInput,
          searchOptions: {
            first: rowsPerPage,
            offset: 0,
            startDate: dateRange.from,
            endDate: dateRange.to,
          },
        },
      });
    },
    [refetch, dateRange, rowsPerPage]
  );

  const onPageChange = useCallback(
    (newPage: number) => {
      setCurrentPage(newPage);
      refetch({
        searchParams: {
          query: customSearchQuery,
          searchOptions: {
            // Ensure the total count stays within MAX_EMAIL_COUNT limit.
            // For eg: if rowsPerPage is 56 and the MAX_EMAIL_COUNT is 9999, the last accessible page will be 179 (9999/56).
            // For the last page, the offset is 9968 ((newPage - 1) * rowsPerPage) and first should be 31 (MAX_EMAIL_COUNT - offset)
            // instead of rowsPerPage, as that 10000th node would not be accessible and will throw an error.
            first:
              newPage * rowsPerPage <= MAX_EMAIL_COUNT ? rowsPerPage : MAX_EMAIL_COUNT - (newPage - 1) * rowsPerPage,
            offset: (newPage - 1) * rowsPerPage,
            startDate: dateRange.from,
            endDate: dateRange.to,
          },
        },
      });
    },
    [refetch, customSearchQuery, rowsPerPage, dateRange]
  );

  const onRowCountChange = useCallback(
    (rowsCount: number) => {
      setCurrentPage(DEFAULT_PAGE);
      setRowsPerPage(rowsCount);
      refetch({
        searchParams: {
          query: customSearchQuery,
          searchOptions: {
            first: rowsCount,
            offset: 0,
            startDate: dateRange.from,
            endDate: dateRange.to,
          },
        },
      });
    },
    [refetch, customSearchQuery, dateRange]
  );

  const handleDateRangeFilter = (value: DateRangeType) => {
    setDateRange({ from: value.from, to: value.to });
    setCurrentPage(DEFAULT_PAGE);
    refetch({
      searchParams: {
        query: customSearchQuery,
        searchOptions: {
          startDate: value.from,
          endDate: value.to,
          first: rowsPerPage,
          offset: 0,
        },
      },
    });
  };

  const formatDateRangeFromTimeStamp = (value: any) => {
    value.to = getFormattedDateFromTimeStamp(value.to);
    value.from = getFormattedDateFromTimeStamp(value.from);

    return value;
  };

  const getDateRange = (value: any) => {
    const rangeValue = formatDateRangeFromTimeStamp(value);

    handleDateRangeFilter(rangeValue);
  };
  const isAnyFilterApplied = !isEqual(dateRange, {});

  const clearAllFilters = () => {
    setCurrentPage(DEFAULT_PAGE);
    setSelectedDateRangeValue('');
    setDateRange({});
    refetch({
      searchParams: {
        query: customSearchQuery,
        searchOptions: {
          first: rowsPerPage,
          offset: 0,
        },
      },
    });
  };

  const getEmailMessage = () => {
    if (isDesignIteration1Enabled) {
      return (
        <div>
          {
            "These emails are for all the accounts you're able to access. Email actions are specific to their related billing account. If you encounter an error, switch to another billing account."
          }
        </div>
      );
    }

    return (
      <div className="details-email-message">
        <div>
          You can view emails for all of the accounts you have access to. Email actions are specific to the related
          billing account. If you encounter an error – switch to another billing account.
        </div>
      </div>
    );
  };

  const renderEmailFlag = () => {
    return (
      <div className="details-email-info">
        <InfoPanel type="information" message={getEmailMessage()} />
      </div>
    );
  };

  const renderFilters = () => {
    if (hideFilters) {
      return <></>;
    }

    return (
      <div className={'ppc-email-table-filter'}>
        {renderCustomSearch()}
        {renderDateFilter()}
        {isAnyFilterApplied && (
          <Button appearance="link" testId="invoice-filter-reset-link" onClick={clearAllFilters}>
            Clear all filters
          </Button>
        )}
      </div>
    );
  };

  const renderCustomSearch = () => {
    return (
      <CustomSearch
        placeholder={SEARCH_PLACEHOLDER_TEXT}
        onSearch={onSearch}
        isLoading={loading}
        isClearable={true}
        helperMessage={SEARCH_BOX_HELPER_MESSAGE}
      />
    );
  };

  const renderDateFilter = () => {
    return (
      <DateFilterWithBadge
        dateValue={getDateRange}
        selected={selectedDateRangeValue}
        onSelected={(selected: string) => {
          setSelectedDateRangeValue(selected);
        }}
        placeholder="Date"
      />
    );
  };

  const getColumnData = (columns: ColumnList[], email: Email) => {
    const { id, subject, attachments, date } = email;
    const emailDetailsLink = getEmailDetailsPageLink(id || '');
    const attachmentId = (attachments && attachments[0]?.id) || '';
    const attachmentFileName = (attachments && attachments[0]?.filename) || '';
    const emailAttachmentRoute = attachmentRoute || '/gateway/api/css-xp-ppc/css-xp-ans/api/1/emails/attachments';

    return columns.map((column) => {
      switch (column.content) {
        case EmailTableColumns.SUBJECT:
          return (
            <EmailSubject
              subject={subject ?? 'No subject'}
              detailsRoute={emailDetailsLink}
              isDesignIteration1Enabled={isDesignIteration1Enabled}
            />
          );
        case EmailTableColumns.ATTACHMENT:
          return hasAttachments(email) ? (
            <EmailAttachment
              attachmentLink={`${emailAttachmentRoute}/${attachmentId}`}
              fileName={attachmentFileName}
              isDesignIteration1Enabled={isDesignIteration1Enabled}
            />
          ) : (
            <td></td>
          );
        case EmailTableColumns.DATE:
          return <td>{date ? <FormattedDate date={date} format={NEW_DATE_TIME_FORMAT} /> : <NoData />}</td>;
      }
    });
  };
  const children = (
    <>
      <ColGroup columns={getHeaders()} />
      <TableHead columns={getHeaders()} />
      <TableBody>
        {
          //@ts-ignore
          emailEdges.map((emailEdge: EmailEdge, index: number) => {
            const email: Email = emailEdge.node;
            return (
              <>
                <tr data-testid={`email-table-row-${index}`} key={`email-table-row-${email.id}`}>
                  {getColumnData(getHeaders(), email).map((node) => node)}
                </tr>
              </>
            );
          })
        }
      </TableBody>
    </>
  );

  const emptyStateProps = isDesignIteration1Enabled ? emptyStatePropForEmailTable : emptyStateProp;
  const emptyStatePropsForSearch = isDesignIteration1Enabled ? emptyStatePropForWrongSearch : emptyStateProp;

  return (
    <CustomDynamicResultTable
      className="ppc-emails-table"
      emptyStateProps={isSearched ? emptyStatePropsForSearch : emptyStateProps}
      isLoading={loading}
      noResults={hasNoResults}
      error={error}
      totalCount={totalCount < MAX_EMAIL_COUNT ? totalCount : MAX_EMAIL_COUNT}
      currentPage={currentPage}
      rowsPerPage={rowsPerPage}
      onPageChange={onPageChange}
      onRowChange={onRowCountChange}
      isPaginationEnabled={
        typeof isPaginationEnabled !== 'undefined' ? isPaginationEnabled : totalCount > DEFAULT_PAGE_SIZE
      }
      innerHeading={isDesignIteration1Enabled ? null : <TableHeading>{label}</TableHeading>}
      testId={testId}
      shouldForceEnableFilters={true}
      errorStateProps={errorStateProp}
      filters={renderFilters()}
      beforeTable={isDesignIteration1EnabledAndNoResults && !isSearched ? null : renderEmailFlag()}
      isSearched={isSearched}
      isAnyFilterApplied={isAnyFilterApplied}
      loaderOptions={hideFilters ? SEARCH_PAGE_LOADER_OPTIONS : {}}
    >
      {children}
    </CustomDynamicResultTable>
  );
};
